import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  ArrowRight,
  Building,
 Layers,
  Loader2,
  Lock,
  Network,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { formatDate } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { useUpdateUser, useUserDetails } from '../api';
import {
  useAllModules,
  useGroups,
  useLanguages,
  useLocations,
  useTtsPlans,
  useUserModules,
  useVoicePlans,
} from '../lookups/api';
import { asFormString, fromLegacyExpiry, toLegacyExpiry } from '../format';
import { updateUserSchema, type UpdateUserInput } from '../schema';
import { ModuleSelector, type ModuleSelectorOption } from '../components/ModuleSelector';
import {
  BULK_REPORT_MODULE_ID,
  DEDICATED_CLI_MODULE_ID,
  INBOUND_MODULE_ID,
  TTS_MODULE_IDS,
  VOICE_PLAN_LOGS_MODULE_ID,
  WEBHOOK_MODULE_ID,
  getModuleMeta,
} from '../components/module-meta';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { useAclModuleGate } from '@/lib/permissions/use-acl-module-gate';
import { MODULE_LISTING_KEYS } from '@/lib/constants/modules';

const STEP_FIELDS: Record<number, Array<keyof UpdateUserInput>> = {
  0: ['name', 'emailid', 'number', 'userType'],
  1: ['company', 'address', 'pincode', 'accountType', 'planType', 'planId', 'expiryDate'],
  // languageIds is validated inside the Modules step (it lives inline
  // under the TTS module — only required when TTS is enabled).
  2: ['moduleIds', 'accessKey', 'secretKey', 'ttsPlanId'],
  3: ['groupIds', 'locationIds', 'campaignLimit', 'campaignHourLimit'],
  4: [],
};

const STEPS = [
  { id: 'contact', label: 'Contact', description: 'Identification', icon: UserRound },
  { id: 'org', label: 'Organization', description: 'Company, plan, validity', icon: Building },
  { id: 'modules', label: 'Modules', description: 'Capabilities', icon: Layers },
  { id: 'allocations', label: 'Allocations', description: 'Groups, locations', icon: Network },
  { id: 'review', label: 'Review', description: 'Confirm changes' },
] as const;

export function UpdateUserPage() {
  const navigate = useNavigate();
  const { userId: targetId } = useParams<{ userId: string }>();
  const targetUserId = Number(targetId);
  const loggedInUserId = Number(useAuthStore((s) => s.userId));
  const role = useAuthStore((s) => s.role);
  const isSubSAdmin = useAuthStore((s) => s.isSubSuperAdmin());
  const ownProfile = useUserDetails(isSubSAdmin ? loggedInUserId : null);
  const parentId = isSubSAdmin && ownProfile.data?.parent
    ? Number(ownProfile.data.parent)
    : loggedInUserId;

  const isSAdmin = role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;

   const profile = useUserDetails(targetUserId);
  // Only the direct parent may change modules, groups, locations, plans and languages.
  const canManage = !profile.data || Number(profile.data.parent) === loggedInUserId;
  const voicePlans = useVoicePlans(loggedInUserId);
  const ttsPlans = useTtsPlans(loggedInUserId);
  // Module picker semantics:
  //   - SADMIN / SUB_SADMIN: grant any platform module
  //   - ADMIN (reseller):    only modules they themselves hold
  const allModules = useAllModules();
  const parentModules = useUserModules(isSAdmin ? null : parentId);
  const modules = isSAdmin ? allModules : parentModules;
  const groups = useGroups();
  const locations = useLocations();
  const languages = useLanguages(parentId);

  const update = useUpdateUser();
  const stepper = useStepper(STEPS.length);
  // const userTypeOptions = isSAdmin
    // ? ([
    //     { value: 'user', label: 'User', hint: 'End customer — runs campaigns directly.' },
    //     { value: 'admin', label: 'Reseller', hint: 'Sub-admin with their own end users.' },
    //   ] as const)
    // : ([{ value: 'user', label: 'User', hint: 'End customer — runs campaigns directly.' }] as const);

  const form = useForm<UpdateUserInput>({
    resolver: zodResolver(updateUserSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      emailid: '',
      number: '',
      company: '',
      address: '',
      pincode: '',
      userType: 'user',
      accountType: '0',
      planType: '0',
      planId: '',
      ttsPlanId: '',
      expiryDate: '',
      // Channels field is no longer exposed in the UI; we hardcode 0 in
      // the submit payload so the legacy endpoint still gets a value.
      channels: '0',
      moduleIds: [],
      accessKey: '',
      secretKey: '',
      ttsCredits: '',
      totalVariable: '',
      groupIds: [],
      locationIds: [],
      languageIds: [],
      campaignLimit: '',
      campaignHourLimit: '',
      isSub: false,
    },
  });

  /* Prefill from /api/obd/user/profile/{targetUserId}. */
  useEffect(() => {
    if (!profile.data) return;
    const p = profile.data;
    form.reset({
      name: asFormString(p.name),
      emailid: asFormString(p.emailid),
      number: asFormString(p.number),
      company: asFormString(p.company),
      address: asFormString(p.address),
      pincode: asFormString(p.pincode),
      // API sends `reseller` for resellers (the form calls them `admin`).
      userType: (p.userType === 'admin' || p.userType === 'reseller'
        ? 'admin'
        : 'user') as UpdateUserInput['userType'],
      accountType: String(p.accountType ?? 0) as '0' | '1' | '3',
      planType: (p.planType ?? '0') as '0' | '1',
      planId: asFormString(p.planId),
      ttsPlanId: asFormString(p.ttsPlanId),
      expiryDate: fromLegacyExpiry(p.expiryDate)?.toISOString() ?? '',
      // Channels removed from the UI; keep '0' so the submit defaults are stable.
      channels: '0',
      // Voice Plan Logs is reseller-only: an end user never keeps it, so it is
      // dropped here and gets disabled for good on the next save.
      moduleIds: (p.modules ?? [])
        .filter(
          (m) =>
            p.userType === 'admin' ||
            p.userType === 'reseller' ||
            String(m.moduleId) !== VOICE_PLAN_LOGS_MODULE_ID,
        )
        .map((m) => String(m.moduleId)),
      accessKey: asFormString(p.accessKey),
      secretKey: asFormString(p.secretKey),
      ttsCredits: asFormString(p.ttsCredits),
      totalVariable: asFormString(p.totalVariable),
      groupIds: (p.group ?? p.groups ?? []).map((g) => String(g.groupId)),
      locationIds: (p.location ?? p.locations ?? []).map((l) => String(l.locationId)),
      languageIds: p.languageCodes ?? [],
      campaignLimit: asFormString(p.campaignLimit),
      campaignHourLimit: asFormString(p.campaignHourLimit),
      isSub: p.sub === 1,
    });
  }, [profile.data, form]);

  const selectedModuleIds = form.watch('moduleIds');
  const selectedUserType = form.watch('userType');

  const ttsEnabled = useMemo(
    () => selectedModuleIds.some((id) => TTS_MODULE_IDS.has(id)),
    [selectedModuleIds],
  );

  const aclAllows = useAclModuleGate();
  const moduleOptions = useMemo<ModuleSelectorOption[]>(
    () =>
      (modules.data ?? [])
        .filter((m) => {
          // Parent's ACL does not grant this module → it can't be handed to a child either.
          if (!aclAllows(m.moduleId)) return false;
          if (String(m.moduleId) === BULK_REPORT_MODULE_ID && selectedUserType !== 'user') return false;
          // Voice Plan Logs is only meant for resellers, never for end users
          if (String(m.moduleId) === VOICE_PLAN_LOGS_MODULE_ID && selectedUserType !== 'admin') return false;
          return true;
        })
        .map((m) => {
          const meta = getModuleMeta(m.moduleId);
          return {
            value: String(m.moduleId),
            label: m.moduleName,
            icon: meta.icon,
            ...(meta.description ? { description: meta.description } : {}),
          };
        }),
    [modules.data, selectedUserType, aclAllows],
  );

  const filteredGroups = useMemo(() => groups.data ?? [], [groups.data]);
  const groupOptions = useMemo(
    () => filteredGroups.map((g) => ({ value: String(g.groupId), label: g.groupName })),
    [filteredGroups],
  );
  const filteredLocations = useModuleFilter(locations.data ?? [], MODULE_LISTING_KEYS.MANAGE_LOCATIONS, (l) => l.locationId);
  const locationOptions = useMemo(
    () =>
      filteredLocations.map((l) => ({
        value: String(l.locationId),
        label: l.locationName,
      })),
    [filteredLocations],
  );
  const languageOptions = useMemo(
    () =>
      (languages.data ?? []).map((l, i) => {
        const code = l.code ?? l.languageCode ?? '';
        const value = code || (l.id != null ? String(l.id) : `lang-${i}`);
        return {
          value,
          label: l.language ?? code ?? `Language ${i + 1}`,
        };
      }),
    [languages.data],
  );

  const handleNext = async () => {
    const fields = [...(STEP_FIELDS[stepper.current] ?? [])];
    // The inline language picker under the TTS module (step 3) must be valid before moving on.
    if (stepper.current === 2 && form.getValues('moduleIds')?.includes('7')) fields.push('languageIds');
    if (!fields || fields.length === 0) {
      stepper.next();
      return;
    }
    const ok = await form.trigger(fields);
    if (ok) stepper.next();
  };

  const onSubmit = (input: UpdateUserInput) => {
    update.mutate(
      {
        userId: targetUserId,
        parent: loggedInUserId,
        username: profile.data?.username ?? '',
        name: input.name.trim(),
        emailid: input.emailid.trim(),
        number: input.number.trim(),
        company: input.company.trim(),
        address: input.address?.trim() ?? '',
        pincode: input.pincode?.trim() ?? '',
        accountType: Number(input.accountType) as 0 | 1 | 3,
        userType: input.userType === 'admin' ? 'reseller' : input.userType,
        planType: input.planType,
        planId: Number(input.planId),
        expiryDate: toLegacyExpiry(input.expiryDate),
        channels: profile.data?.channels ?? 0,
       moduleRows: JSON.stringify({
          moduleList: canManage
            ? input.moduleIds
                .filter((id) => input.userType === 'admin' || id !== VOICE_PLAN_LOGS_MODULE_ID)
                .map((id) => ({ moduleId: id }))
            : // Not the parent: send the modules exactly as stored so nothing changes.
              (profile.data?.modules ?? []).map((m) => ({ moduleId: String(m.moduleId) })),
        }),
        accessKey: input.moduleIds.includes(WEBHOOK_MODULE_ID) ? input.accessKey : undefined,
        secretKey: input.moduleIds.includes(WEBHOOK_MODULE_ID) ? input.secretKey : undefined,
        ttsPlanId: ttsEnabled && input.ttsPlanId ? Number(input.ttsPlanId) : undefined,
        // ttsCredits / totalVariable inputs were removed from the UI;
        // send legacy-friendly defaults so the endpoint contract holds.
        ttsCredits: '0',
        totalVariable: undefined,
        // Legacy wire: groupRows/locationRows are JSON-stringified OBJECTS
        // wrapping the array — `{"groupsList":[…]}` / `{"locationsList":[…]}`.
        // Backend does `new JSONObject(...)` and rejects bare arrays.
        groupRows:
  input.groupIds.length > 0
    ? JSON.stringify({
        groupsList: input.groupIds.map((id) => {
          const g = groupOptions.find((o) => o.value === id);
          return { groupId: Number(id), groupName: g?.label ?? '' };
        }),
      })
    : JSON.stringify({ groupsList: [] }),
locationRows:
  input.locationIds.length > 0
    ? JSON.stringify({
        locationsList: input.locationIds.map((id) => {
          const l = locationOptions.find((o) => o.value === id);
          return { locationId: Number(id), locationName: l?.label ?? '' };
        }),
      })
    : JSON.stringify({ locationsList: [] }),
        languages: ttsEnabled && input.languageIds.length > 0 ? input.languageIds.join(',') : undefined,
        campaignLimit:
          input.userType === 'user' && input.campaignLimit
            ? Number(input.campaignLimit)
            : undefined,
        campaignHourLimit:
          input.userType === 'user' && input.campaignHourLimit
            ? Number(input.campaignHourLimit)
            : undefined,
        sub: input.isSub ? 1 : 0,
        userId2: profile.data?.userId2 ? Number(profile.data.userId2) : 0,
        ...(isSubSAdmin && profile.data?.aclId ? { aclId: Number(profile.data.aclId) } : {}),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'User updated');
          navigate('/user-list');
        },
        onError: (err) => {
          toast.error(readErrorMessage(err) ?? 'Could not update the user.');
        },
      },
    );
  };

  const renderModuleExtras = (moduleId: string): React.ReactNode => {
    if (TTS_MODULE_IDS.has(moduleId)) {
      return (
        <>
          <FormField
            control={form.control}
            name="ttsPlanId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  TTS plan<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <Select
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                   disabled={ttsPlans.isLoading || !canManage}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={ttsPlans.isLoading ? 'Loading…' : 'Pick a TTS plan'}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(ttsPlans.data ?? []).map((p) => (
                      <SelectItem key={p.planId} value={String(p.planId)}>
                        {p.planName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="languageIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Languages<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <MultiSelect
                    options={languageOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    placeholder={
                      languages.isLoading ? 'Loading…' : 'Pick at least one language'
                    }
                    disabled={languages.isLoading || !canManage}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      );
    }
    if (moduleId === WEBHOOK_MODULE_ID) {
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="accessKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Access key</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="ak_..." />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="secretKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secret key</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="sk_..." />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      );
    }
    return null;
  };

  const moduleWarning = (id: string): string | null => {
    if (id === INBOUND_MODULE_ID && !selectedModuleIds.includes(DEDICATED_CLI_MODULE_ID)) {
      return 'Inbound campaigns also need the Dedicated CLI module enabled.';
    }
    return null;
  };

  if (profile.isLoading) {
    return (
      <ScrollPage>
        <PageHeader title="Update User" description="Loading…" />
        <FormSkeleton />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Update User"
        description={
          profile.data
            ? `Edit ${profile.data.username}'s account, plan, modules, and allocations.`
            : 'Edit a user.'
        }
        actions={
          <Button asChild variant="outline">
            <Link to="/user-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <Stepper
            steps={[...STEPS]}
            current={stepper.current}
            completed={stepper.completed}
            onStepClick={(i) => i <= stepper.current && stepper.goTo(i)}
          />
        </CardContent>
      </Card>

      <Form {...form}>
      <form className="space-y-4" noValidate>
          {!canManage && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Modules, groups, locations, plans and languages can only be changed by this user's
                parent{profile.data?.parentName ? ` (${profile.data.parentName})` : ''}. You can view
                them here.
              </span>
            </div>
          )}
          {stepper.current === 0 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Contact</CardTitle>
                  <CardDescription>
                    Identification and direct contact details. Username and password are managed
                    separately.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  {profile.data?.username && (
                    <div className="md:col-span-2">
                      <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Username
                      </div>
                      <div className="mt-0.5 text-sm font-medium text-foreground">
                        @{profile.data.username}
                      </div>
                    </div>
                  )}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Full name<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="emailid"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Email<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Phone<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))
                            }
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="10-digit mobile number"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* <Card>
                <CardHeader>
                  <CardTitle>Role</CardTitle>
                  <CardDescription>
                    {isSAdmin
                      ? 'Reseller users can manage their own end-users.'
                      : 'This user is an end customer who runs campaigns directly.'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="userType"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={!isSAdmin}
                            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                          >
                            {userTypeOptions.map((opt) => (
                              <label
                                key={opt.value}
                                className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 hover:border-ring"
                              >
                                <RadioGroupItem value={opt.value} className="mt-0.5" />
                                <div>
                                  <div className="text-sm font-medium">{opt.label}</div>
                                  <div className="text-xs text-muted-foreground">{opt.hint}</div>
                                </div>
                              </label>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card> */}
            </>
          )}

          {stepper.current === 1 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Organization</CardTitle>
                  <CardDescription>Company and mailing address.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>
                          Company<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>
                          Address<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pincode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Pincode<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} inputMode="numeric" maxLength={10} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Plan &amp; validity</CardTitle>
                  <CardDescription>Billing mode, voice plan, and validity window.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="accountType"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>
                          Account type<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            className="grid grid-cols-2 gap-3"
                          >
                            {(
                              [
                                { value: '0', label: 'Promotional' },
                                { value: '1', label: 'Transactional' },
                                // { value: '3', label: 'Both (Promotional + Transactional)' },
                              ] as const
                            ).map((opt) => (
                              <label
                                key={opt.value}
                                className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 hover:border-ring"
                              >
                                <RadioGroupItem value={opt.value} />
                                <span className="text-sm font-medium">{opt.label}</span>
                              </label>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="planType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Billing mode<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">Prepaid</SelectItem>
                            {field.value === '1' && <SelectItem value="1">Postpaid</SelectItem>}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="planId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Voice plan<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                         disabled={voicePlans.isLoading || !canManage}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={voicePlans.isLoading ? 'Loading…' : 'Pick a voice plan'}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(voicePlans.data ?? []).map((p) => (
                              <SelectItem key={p.planId} value={String(p.planId)}>
                                {p.planName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="expiryDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Validity expires on<span className="ml-0.5 text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            value={field.value ? new Date(field.value) : null}
                            onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                            fromDate={new Date()}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </>
          )}

          {stepper.current === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Modules &amp; capabilities</CardTitle>
                <CardDescription>
                  Toggle modules on or off. Each module's settings appear inline once enabled.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="moduleIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ModuleSelector
                          options={moduleOptions}
                          value={field.value}
                          onChange={field.onChange}
                          renderExtras={renderModuleExtras}
                          getWarning={moduleWarning}
                          disabled={modules.isLoading || !canManage}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 3 && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Resources</CardTitle>
                  <CardDescription>
                    Select the groups and locations this user can use. At least one
                    group and one location are required.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="groupIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Groups</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={groupOptions}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select group(s)"
                            searchPlaceholder="Filter groups…"
                            disabled={!canManage}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="locationIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Locations</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={locationOptions}
                            value={field.value}
                            onChange={field.onChange}
                              placeholder="Select location(s)"
                            searchPlaceholder="Filter locations…"
                            disabled={!canManage}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {isSAdmin && selectedUserType === 'user' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Campaign limits</CardTitle>
                    <CardDescription>Optional throttles for campaign creation.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="campaignLimit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Total active campaigns</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              inputMode="numeric"
                              min="0"
                              placeholder="No limit"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {isSAdmin && (
                      <FormField
                        control={form.control}
                        name="campaignHourLimit"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Campaigns per hour</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="number"
                                inputMode="numeric"
                                min="0"
                                placeholder="No limit"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {stepper.current === 4 && (
            <ReviewStep
              form={form}
              username={profile.data?.username ?? ''}
              voicePlans={voicePlans.data ?? []}
              ttsPlans={ttsPlans.data ?? []}
              moduleOptions={moduleOptions}
              groupOptions={groupOptions}
              locationOptions={locationOptions}
              languageOptions={languageOptions}
              ttsEnabled={ttsEnabled}
            />
          )}

          <div className="sticky bottom-0 -mx-4 mt-2 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.isFirst || update.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-xs text-muted-foreground">
              Step {stepper.current + 1} of {STEPS.length}
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost">
                <Link to="/user-list">Cancel</Link>
              </Button>
                            <Button
                type="button"
                disabled={update.isPending}
                onClick={async () => {
                  if (!stepper.isLast) {
                    await handleNext();
                  } else {
                    form.handleSubmit(onSubmit)();
                  }
                }}
              >
                {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {stepper.isLast ? 'Save changes' : <>Next <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

interface ReviewStepProps {
  form: ReturnType<typeof useForm<UpdateUserInput>>;
  username: string;
  voicePlans: Array<{ planId: number; planName: string }>;
  ttsPlans: Array<{ planId: number; planName: string }>;
  moduleOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
  locationOptions: Array<{ value: string; label: string }>;
  languageOptions: Array<{ value: string; label: string }>;
  ttsEnabled: boolean;
}

function ReviewStep({
  form,
  username,
  voicePlans,
  ttsPlans,
  moduleOptions,
  groupOptions,
  locationOptions,
  languageOptions,
  ttsEnabled,
}: ReviewStepProps) {
  const role = useAuthStore((s) => s.role);
  const isSAdmin = role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;
  const v = form.getValues();
  const expiry = v.expiryDate ? formatDate(new Date(v.expiryDate)) : '—';
  const planName = voicePlans.find((p) => String(p.planId) === v.planId)?.planName ?? '—';
  const ttsPlanName = ttsPlans.find((p) => String(p.planId) === v.ttsPlanId)?.planName ?? '—';
  const moduleLabels = v.moduleIds
    .map((id) => moduleOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const groupLabels = v.groupIds
    .map((id) => groupOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const locationLabels = v.locationIds
    .map((id) => locationOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const languageLabels = v.languageIds
    .map((id) => languageOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm changes before saving. Click any earlier step header to edit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReviewSection title="Contact">
          <Row label="Username" value={username ? `@${username}` : '—'} />
          <Row label="Full name" value={v.name} />
          <Row label="Email" value={v.emailid} />
          <Row label="Phone" value={v.number} />
          <Row label="User type" value={v.userType === 'admin' ? 'Reseller' : 'User'} />
        </ReviewSection>
        <ReviewSection title="Organization">
          <Row label="Company" value={v.company} />
          <Row label="Address" value={v.address ?? ''} multiline />
          <Row label="Pincode" value={v.pincode ?? ''} />
        </ReviewSection>
        <ReviewSection title="Plan & validity">
          <Row label="Account type" value={v.accountType === '3' ? 'Both' : v.accountType === '1' ? 'Transactional' : 'Promotional'} />
          <Row label="Billing mode" value={v.planType === '1' ? 'Postpaid' : 'Prepaid'} />
          <Row label="Voice plan" value={planName} />
          <Row label="Validity expires" value={expiry} />
        </ReviewSection>
        <ReviewSection title="Modules">
          <Row label="Enabled modules" value={moduleLabels || 'None'} multiline />
          {ttsEnabled && (
            <>
              <Row label="TTS plan" value={ttsPlanName} />
              <Row label="Languages" value={languageLabels || '—'} multiline />
            </>
          )}
          {v.moduleIds.includes('3') && (
            <>
              <Row label="Webhook access key" value={v.accessKey ?? '—'} />
              <Row label="Webhook secret key" value={v.secretKey ? '••••••••' : '—'} />
            </>
          )}
        </ReviewSection>
        <ReviewSection title="Allocations">
          <Row label="Groups" value={groupLabels || 'Any'} multiline />
          <Row label="Locations" value={locationLabels || 'Any'} multiline />
          {isSAdmin && v.userType === 'user' && (
            <>
              <Row label="Campaign limit" value={v.campaignLimit || 'No limit'} />
              <Row label="Campaigns per hour" value={v.campaignHourLimit || 'No limit'} />
            </>
          )}
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{children}</dl>
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'md:col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium text-foreground ${multiline ? 'whitespace-pre-line' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map((s) => (
        <Card key={s}>
          <CardContent className="space-y-3 p-6">
            <Skeleton className="h-5 w-1/4" />
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}